import {
  useState,
  useRef,
  useEffect,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import ReactMarkdown from "react-markdown";
import type { PlaybackState } from "../hooks/useVoiceOutput";
import type { VoiceInputState } from "../hooks/useVoiceInput";
import type { ConversationUIState } from "../types";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled: boolean;
  primaryColor: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  footerText?: string | null;
  disableSendMessage?: boolean;
  voiceEnabled?: boolean;
  isRecording?: boolean;
  onStartRecording?: () => void;
  playbackState?: PlaybackState;
  voiceInputState?: VoiceInputState;
  conversationUIState?: ConversationUIState;
  onStopAndSubmit?: () => void;
  onExitConversationMode?: () => void;
}

interface ListeningBarsProps {
  primaryColor: string;
}

function ListeningBars({ primaryColor }: ListeningBarsProps) {
  return (
    <div className="flex h-4 items-center gap-[3px]" aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <div
          key={delay}
          className="h-3 w-[3px] origin-center rounded-sm"
          style={{
            backgroundColor: primaryColor,
            animation: `voice-bar-pulse 1s ease-in-out infinite`,
            animationDelay: `${delay}ms`,
          }}
        />
      ))}
    </div>
  );
}

interface ListeningInputProps {
  primaryColor: string;
  onExitConversationMode?: () => void;
}

function ListeningInput({
  primaryColor,
  onExitConversationMode,
}: ListeningInputProps) {
  return (
    <>
      <div
        className="flex min-h-12 flex-1 items-center gap-2.5 rounded-[10px] border-2 bg-(--primary)/8 border-(--primary) p-3"
        style={{ "--primary": primaryColor } as React.CSSProperties}
        aria-label="Listening for your voice"
      >
        <ListeningBars primaryColor={primaryColor} />
        <span
          className="font-sans text-sm font-normal text-(--primary)"
          style={{ "--primary": primaryColor } as React.CSSProperties}
        >
          Listening...
        </span>
      </div>
      <button
        type="button"
        onClick={onExitConversationMode}
        aria-label="Exit conversation mode"
        className="flex h-12 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-none bg-transparent px-4 text-sm font-medium text-(--primary) hover:underline"
        style={{ "--primary": primaryColor } as React.CSSProperties}
      >
        Exit
      </button>
    </>
  );
}

interface ConversationActiveInputProps {
  primaryColor: string;
  conversationUIState?: ConversationUIState;
  onExitConversationMode?: () => void;
}

function ConversationActiveInput({
  primaryColor,
  conversationUIState,
  onExitConversationMode,
}: ConversationActiveInputProps) {
  const isProcessing = conversationUIState === "conversation-processing";
  const statusLabel = isProcessing ? "Processing..." : "Speaking...";
  const ariaLabel = isProcessing
    ? "Processing your voice"
    : "Speaking voice response";

  return (
    <>
      <div
        data-testid="voice-mode-active-input"
        className="flex min-h-12 flex-1 items-center gap-2.5 rounded-[10px] border-2 bg-(--primary)/8 border-(--primary) p-3"
        style={{ "--primary": primaryColor } as React.CSSProperties}
        aria-label={ariaLabel}
      >
        <span className="font-sans text-sm font-normal text-(--primary)">
          {statusLabel}
        </span>
      </div>
      <button
        type="button"
        onClick={onExitConversationMode}
        aria-label="Exit conversation mode"
        className="flex h-12 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-none bg-transparent px-4 text-sm font-medium text-(--primary) hover:underline"
        style={{ "--primary": primaryColor } as React.CSSProperties}
      >
        Exit
      </button>
    </>
  );
}

function getVoiceAnnouncement({
  conversationUIState,
  isTranscribing,
  isVoiceError,
  isMicBlocked,
}: {
  conversationUIState?: ConversationUIState;
  isTranscribing: boolean;
  isVoiceError: boolean;
  isMicBlocked: boolean;
}): string {
  if (isVoiceError) return "Could not transcribe audio";
  if (isMicBlocked)
    return "Microphone blocked. Enable it in your browser settings.";
  if (conversationUIState === "conversation-listening") return "Listening...";
  if (conversationUIState === "conversation-processing") return "Processing...";
  if (conversationUIState === "conversation-speaking") return "Speaking...";
  if (isTranscribing) return "Transcribing audio...";
  return "";
}

interface DefaultInputProps {
  message: string;
  setMessage: (value: string) => void;
  handleSubmit: (e: FormEvent) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  disabled: boolean;
  disableSendMessage?: boolean;
  primaryColor: string;
  voiceEnabled?: boolean;
  isRecording?: boolean;
  isTranscribing: boolean;
  isVoiceError: boolean;
  isMicBlocked: boolean;
  isPlaybackActive: boolean;
  handleMicClick: () => void;
}

function DefaultInput({
  message,
  setMessage,
  handleSubmit,
  handleKeyDown,
  textareaRef,
  disabled,
  disableSendMessage,
  primaryColor,
  voiceEnabled,
  isRecording,
  isTranscribing,
  isVoiceError,
  isMicBlocked,
  isPlaybackActive,
  handleMicClick,
}: DefaultInputProps) {
  return (
    <>
      <label htmlFor="message-input" className="sr-only">
        Type your message
      </label>
      <textarea
        ref={textareaRef}
        id="message-input"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask me anything"
        disabled={disabled || disableSendMessage || isTranscribing}
        rows={1}
        className="min-h-8 max-h-32 flex-1 resize-none overflow-y-hidden rounded border border-transparent bg-transparent py-1 text-base leading-tight text-black placeholder:text-[#5A5A5A] focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Message input"
        aria-required="true"
      />
      {voiceEnabled && (
        <div className="relative flex shrink-0 flex-col items-center">
          <button
            type="button"
            onClick={handleMicClick}
            disabled={disabled || disableSendMessage || isTranscribing}
            aria-label={isRecording ? "Stop recording" : "Start recording"}
            title={isRecording ? "Stop recording" : "Start recording"}
            aria-pressed={isRecording}
            className={`relative flex h-8 w-8 items-center justify-center rounded bg-white text-gray-700 transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 ${
              isRecording ? "animate-pulse" : ""
            }`}
          >
            {isTranscribing ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="animate-spin"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="28"
                  strokeDashoffset="10"
                />
              </svg>
            ) : isRecording ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="4" y="4" width="8" height="8" rx="1" />
              </svg>
            ) : (
              <div
                className="flex items-center gap-[2px]"
                aria-hidden="true"
                style={{ height: 14 }}
              >
                <div
                  className="w-[2px] rounded-sm bg-current"
                  style={{ height: 4 }}
                />
                <div
                  className="w-[2px] rounded-sm bg-current"
                  style={{ height: 8 }}
                />
                <div
                  className="w-[2px] rounded-sm bg-current"
                  style={{ height: 14 }}
                />
                <div
                  className="w-[2px] rounded-sm bg-current"
                  style={{ height: 8 }}
                />
                <div
                  className="w-[2px] rounded-sm bg-current"
                  style={{ height: 4 }}
                />
              </div>
            )}
            {isPlaybackActive && !isRecording && (
              <span
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500"
                aria-hidden="true"
              />
            )}
          </button>
          {(isVoiceError || isMicBlocked) && (
            <span
              className="absolute top-9 right-0 whitespace-nowrap rounded bg-red-100 px-2 py-0.5 text-xs text-red-700"
              role="alert"
            >
              {isMicBlocked
                ? "Microphone blocked. Enable it in your browser settings."
                : "Could not transcribe"}
            </span>
          )}
        </div>
      )}
      <button
        type="submit"
        disabled={disabled || !message.trim() || disableSendMessage}
        aria-label="Send message"
        style={{
          backgroundColor:
            disabled || !message.trim() || disableSendMessage
              ? `${primaryColor}99`
              : primaryColor,
        }}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path
            d="M8 12L8 4M8 4L4 8M8 4L12 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
}

export function MessageInput({
  onSendMessage,
  disabled,
  primaryColor,
  textareaRef: externalTextareaRef,
  footerText,
  disableSendMessage,
  voiceEnabled,
  isRecording,
  onStartRecording,
  playbackState,
  voiceInputState,
  conversationUIState,
  onStopAndSubmit: _onStopAndSubmit,
  onExitConversationMode,
}: MessageInputProps) {
  const [message, setMessage] = useState("");
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalTextareaRef || internalTextareaRef;

  useEffect(() => {
    if (textareaRef.current) {
      if (message.trim() === "") {
        textareaRef.current.style.height = "32px";
      } else {
        textareaRef.current.style.height = "0px";
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = `${Math.max(32, scrollHeight)}px`;
      }
    }
  }, [message]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !disableSendMessage) {
      onSendMessage(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleMicClick = () => onStartRecording?.();

  const isPlaybackActive =
    playbackState === "loading" || playbackState === "playing";
  const isTranscribing = voiceInputState === "transcribing";
  const isVoiceError = voiceInputState === "error";
  const isMicBlocked = voiceInputState === "permission-denied";

  return (
    <form
      onSubmit={handleSubmit}
      className="shrink-0 border-t border-[#E0E0E0] bg-white px-6 pb-6 pt-4"
    >
      <div className="flex items-center gap-2">
        {conversationUIState === "conversation-listening" ? (
          <ListeningInput
            primaryColor={primaryColor}
            onExitConversationMode={onExitConversationMode}
          />
        ) : conversationUIState === "conversation-processing" ||
          conversationUIState === "conversation-speaking" ? (
          <ConversationActiveInput
            primaryColor={primaryColor}
            conversationUIState={conversationUIState}
            onExitConversationMode={onExitConversationMode}
          />
        ) : (
          <DefaultInput
            message={message}
            setMessage={setMessage}
            handleSubmit={handleSubmit}
            handleKeyDown={handleKeyDown}
            textareaRef={textareaRef}
            disabled={disabled}
            disableSendMessage={disableSendMessage}
            primaryColor={primaryColor}
            voiceEnabled={voiceEnabled}
            isRecording={isRecording}
            isTranscribing={isTranscribing}
            isVoiceError={isVoiceError}
            isMicBlocked={isMicBlocked}
            isPlaybackActive={isPlaybackActive}
            handleMicClick={handleMicClick}
          />
        )}
      </div>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {getVoiceAnnouncement({
          conversationUIState,
          isTranscribing,
          isVoiceError,
          isMicBlocked,
        })}
      </div>
      {footerText && (
        <div className="mt-2 text-xs text-[#5A5A5A]">
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => <p className="m-0" {...props} />,
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  className="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          >
            {footerText}
          </ReactMarkdown>
        </div>
      )}
    </form>
  );
}
